import { resolveAttachmentUri } from '../../../../shared/utils/resolveMediaUri';
import { planImageFromCatalogPlanLike } from '../planImage';
import { sortByDisplayOrder, type CategoryDaysItem } from './subscriptionData';

/** First non-empty plan image in regular category hierarchy (subscriptions → type_data). */
export function firstPlanImageFromRegularCategories(categories: unknown[] | null | undefined): string | null {
  if (categories == null) {
    return null;
  }
  for (const cat of categories) {
    const cat_record = cat as { subscriptions?: Array<{ type_data?: unknown[] }> };
    for (const sub of cat_record.subscriptions ?? []) {
      for (const plan of sub.type_data ?? []) {
        const uri = planImageFromCatalogPlanLike(plan);
        if (uri) {
          return uri;
        }
      }
    }
  }
  return null;
}

/** First plan image in Flow 2 category (days → tag groups → plans), display order. */
export function firstPlanImageFromCategoryDaysItem(category: CategoryDaysItem | null | undefined): string | null {
  if (category == null) {
    return null;
  }
  for (const day_entry of category.days ?? []) {
    for (const tagged of day_entry.tagged_plans ?? []) {
      for (const plan of tagged.plans ?? []) {
        const uri = planImageFromCatalogPlanLike(plan);
        if (uri) {
          return uri;
        }
      }
    }
  }
  return null;
}

export function firstPlanImageFromCustomCategory(cat: unknown): string | null {
  if (cat == null || typeof cat !== 'object') {
    return null;
  }
  const cat_record = cat as {
    particulars?: Array<{ display_order?: number; displayOrder?: number }>;
    subscriptions?: Array<{ type_data?: unknown[] }>;
  };
  const particulars = sortByDisplayOrder(cat_record.particulars ?? []);
  for (const p of particulars) {
    const uri = planImageFromCatalogPlanLike(p);
    if (uri) {
      return uri;
    }
  }
  for (const sub of cat_record.subscriptions ?? []) {
    for (const plan of sub.type_data ?? []) {
      const uri = planImageFromCatalogPlanLike(plan);
      if (uri) {
        return uri;
      }
    }
  }
  return null;
}

export function resolveBannerPopupImageUri(
  banner_data: Record<string, unknown>,
  fallback_uri?: string | null,
): string | null {
  return (
    resolveAttachmentUri(banner_data.attachment) ??
    resolveAttachmentUri(banner_data.thumbnail) ??
    resolveAttachmentUri((banner_data as { tag_attachment?: unknown }).tag_attachment) ??
    fallback_uri ??
    null
  );
}

type TaggedCategoriesData =
  | { tagged_categories: Array<{ tag_id: string; categories: unknown[] }> }
  | null
  | undefined;

export function firstPlanImageForTagSlide(
  tag_id: string,
  restructured: TaggedCategoriesData,
  regular_plans: unknown[],
): string | null {
  if (String(tag_id) === 'all-0') {
    return firstPlanImageFromRegularCategories(regular_plans);
  }
  const tagged = restructured?.tagged_categories?.find(t => String(t.tag_id) === String(tag_id));
  if (tagged == null) {
    return null;
  }
  for (const cat of tagged.categories ?? []) {
    const days = (cat as { days?: Array<{ subscriptions?: unknown[] }> }).days;
    if (!Array.isArray(days)) {
      continue;
    }
    for (const day_entry of days) {
      for (const plan of day_entry.subscriptions ?? []) {
        const uri = planImageFromCatalogPlanLike(plan);
        if (uri) {
          return uri;
        }
      }
    }
  }
  return null;
}
